// Mailchimp utility functions for frontend integration

const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? 'https://membership.openorg.fyi' 
  : 'http://localhost:3001';

// Default list ID from environment variable
const DEFAULT_LIST_ID = process.env.REACT_APP_MAILCHIMP_LIST_ID;

/**
 * Helper function to build merge fields object
 * @param {Object} userData - User data object
 * @returns {Object} Formatted merge fields for Mailchimp
 */
const buildMergeFields = (userData = {}) => {
  const mergeFields = {};
  
  // Basic fields
  if (userData.firstName) mergeFields.FNAME = userData.firstName;
  if (userData.lastName) mergeFields.LNAME = userData.lastName;
  
  // Address (can be string or object)
  if (userData.address) {
    if (typeof userData.address === 'string') {
      mergeFields.ADDRESS = userData.address;
    } else if (typeof userData.address === 'object') {
      // Format address object to string
      const { street, city, state, zip, country } = userData.address;
      const addressParts = [street, city, state, zip, country].filter(Boolean);
      mergeFields.ADDRESS = addressParts.join(', ');
    }
  }
  
  // Phone number
  if (userData.phone) mergeFields.PHONE = userData.phone;
  
  // LinkedIn Profile URL
  if (userData.linkedinUrl) mergeFields.MMERGE5 = userData.linkedinUrl;
  
  // Goal
  if (userData.goal) mergeFields.MMERGE6 = userData.goal;
  
  // Subscription dates (format as MM/DD/YYYY)
  if (userData.subscriptionStartDate) {
    const startDate = new Date(userData.subscriptionStartDate);
    mergeFields.MMERGE7 = startDate.toLocaleDateString('en-US');
  }
  
  if (userData.subscriptionEndDate) {
    const endDate = new Date(userData.subscriptionEndDate);
    mergeFields.MMERGE8 = endDate.toLocaleDateString('en-US');
  }
  
  // Stripe Customer ID
  if (userData.stripeCustomerId) mergeFields.MMERGE9 = userData.stripeCustomerId;
  
  // Membership Plan
  if (userData.membershipPlan) mergeFields.MMERGE10 = userData.membershipPlan;
  
  // Supabase UUID
  if (userData.supabaseId || userData.id) {
    mergeFields.MMERGE11 = userData.supabaseId || userData.id;
  }
  
  return mergeFields;
};

/**
 * Subscribe a user to a Mailchimp list
 * @param {Object} params - Subscription parameters
 * @param {string} params.email - User's email address
 * @param {string} [params.listId] - Mailchimp list ID (defaults to env variable)
 * @param {string} [params.firstName] - User's first name
 * @param {string} [params.lastName] - User's last name
 * @param {string[]} [params.tags] - Array of tags to apply
 * @param {Object} [params.mergeFields] - Additional merge fields
 * @returns {Promise<Object>} Response from Mailchimp API
 */
export const subscribeToMailchimp = async ({
  email,
  listId = DEFAULT_LIST_ID,
  firstName = '',
  lastName = '',
  tags = [],
  mergeFields = {}
}) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/mailchimp/subscribe`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email,
        listId,
        firstName,
        lastName,
        tags,
        mergeFields
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to subscribe to Mailchimp');
    }

    return data;
  } catch (error) {
    console.error('Error subscribing to Mailchimp:', error);
    throw error;
  }
};

/**
 * Update a Mailchimp list member
 * @param {Object} params - Update parameters
 * @param {string} params.email - User's email address
 * @param {string} [params.listId] - Mailchimp list ID (defaults to env variable)
 * @param {string} [params.firstName] - User's first name
 * @param {string} [params.lastName] - User's last name
 * @param {string[]} [params.tags] - Array of tags to apply
 * @param {Object} [params.mergeFields] - Additional merge fields
 * @param {string} [params.status] - Member status (subscribed, unsubscribed, etc.)
 * @returns {Promise<Object>} Response from Mailchimp API
 */
export const updateMailchimpMember = async ({
  email,
  listId = DEFAULT_LIST_ID,
  firstName,
  lastName,
  tags = [],
  mergeFields = {},
  status
}) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/mailchimp/update-member`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email,
        listId,
        firstName,
        lastName,
        tags,
        mergeFields,
        status
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to update Mailchimp member');
    }

    return data;
  } catch (error) {
    console.error('Error updating Mailchimp member:', error);
    throw error;
  }
};

/**
 * Get a Mailchimp list member
 * @param {string} email - User's email address
 * @param {string} [listId] - Mailchimp list ID (defaults to env variable)
 * @returns {Promise<Object>} Member data from Mailchimp API
 */
export const getMailchimpMember = async (email, listId = DEFAULT_LIST_ID) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/mailchimp/member/${listId}/${email}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    const data = await response.json();

    if (!response.ok) {
      if (response.status === 404) {
        return null; // Member not found
      }
      throw new Error(data.error || 'Failed to get Mailchimp member');
    }

    return data.member;
  } catch (error) {
    console.error('Error getting Mailchimp member:', error);
    throw error;
  }
};

/**
 * Subscribe user with comprehensive data mapping
 * @param {Object} userData - Complete user data object
 * @param {string} userData.email - User's email address
 * @param {string} [userData.firstName] - User's first name
 * @param {string} [userData.lastName] - User's last name
 * @param {string|Object} [userData.address] - User's address (string or object)
 * @param {string} [userData.phone] - User's phone number
 * @param {string} [userData.linkedinUrl] - LinkedIn profile URL
 * @param {string} [userData.goal] - User's goal
 * @param {string|Date} [userData.subscriptionStartDate] - Subscription start date
 * @param {string|Date} [userData.subscriptionEndDate] - Subscription end date
 * @param {string} [userData.stripeCustomerId] - Stripe customer ID
 * @param {string} [userData.membershipPlan] - Membership plan
 * @param {string} [userData.supabaseId] - Supabase UUID
 * @param {string[]} [userData.tags] - Array of tags to apply
 * @param {string} [listId] - Mailchimp list ID (defaults to env variable)
 * @returns {Promise<Object>} Subscription result
 */
export const subscribeUserWithFullData = async (userData, listId = DEFAULT_LIST_ID) => {
  try {
    // Build merge fields from user data
    const mergeFields = buildMergeFields(userData);
    
    const result = await subscribeToMailchimp({
      email: userData.email,
      listId,
      firstName: userData.firstName || '',
      lastName: userData.lastName || '',
      tags: userData.tags || [],
      mergeFields
    });

    // console.log('Successfully subscribed user with full data:', result);
    return result;
  } catch (error) {
    console.error('Failed to subscribe user with full data:', error);
    throw error;
  }
};

/**
 * Subscribe user after successful payment
 * @param {Object} user - User object from Supabase
 * @param {Object} [paymentData] - Additional payment data
 * @param {string} [listId] - Mailchimp list ID (defaults to env variable)
 * @returns {Promise<Object>} Subscription result
 */
export const subscribeAfterPayment = async (user, paymentData = {}, listId = DEFAULT_LIST_ID) => {
  try {
    // Prepare comprehensive user data for subscription
    const userData = {
      email: user.email,
      firstName: user.first_name || user.user_metadata?.first_name || '',
      lastName: user.last_name || user.user_metadata?.last_name || '',
      address: user.address,
      phone: user.phone,
      linkedinUrl: user.linkedin_url,
      goal: user.goal,
      subscriptionStartDate: paymentData.subscriptionStartDate || new Date(),
      subscriptionEndDate: paymentData.subscriptionEndDate,
      stripeCustomerId: paymentData.stripeCustomerId,
      membershipPlan: paymentData.membershipPlan || 'annual',
      supabaseId: user.id,
      tags: ['paid-member', 'active-subscription']
    };

    const result = await subscribeUserWithFullData(userData, listId);
    // console.log('Successfully subscribed user after payment:', result);
    return result;
  } catch (error) {
    console.error('Failed to subscribe user after payment:', error);
    // Don't throw here - payment success shouldn't fail due to Mailchimp issues
    return { success: false, error: error.message };
  }
};

/**
 * Update member with comprehensive data
 * @param {Object} userData - Complete user data object (same format as subscribeUserWithFullData)
 * @param {string} [listId] - Mailchimp list ID (defaults to env variable)
 * @returns {Promise<Object>} Update result
 */
export const updateUserWithFullData = async (userData, listId = DEFAULT_LIST_ID) => {
  try {
    // Build merge fields from user data
    const mergeFields = buildMergeFields(userData);
    
    const result = await updateMailchimpMember({
      email: userData.email,
      listId,
      firstName: userData.firstName || '',
      lastName: userData.lastName || '',
      tags: userData.tags || [],
      mergeFields,
      status: userData.status
    });

    // console.log('Successfully updated user with full data:', result);
    return result;
  } catch (error) {
    console.error('Failed to update user with full data:', error);
    throw error;
  }
};

/**
 * Update member status when subscription changes
 * @param {Object} user - User object from Supabase
 * @param {boolean} isActive - Whether subscription is active
 * @param {Object} [additionalData] - Additional data to update
 * @param {string} [listId] - Mailchimp list ID (defaults to env variable)
 * @returns {Promise<Object>} Update result
 */
export const updateSubscriptionStatus = async (user, isActive, additionalData = {}, listId = DEFAULT_LIST_ID) => {
  try {
    const tags = isActive 
      ? ['paid-member', 'active-subscription']
      : ['paid-member', 'inactive-subscription'];

    // Prepare comprehensive user data for update
    const userData = {
      email: user.email,
      firstName: user.first_name || user.user_metadata?.first_name || '',
      lastName: user.last_name || user.user_metadata?.last_name || '',
      address: user.address,
      phone: user.phone,
      linkedinUrl: user.linkedin_url,
      goal: user.goal,
      subscriptionStartDate: additionalData.subscriptionStartDate,
      subscriptionEndDate: isActive ? additionalData.subscriptionEndDate : new Date(),
      stripeCustomerId: additionalData.stripeCustomerId,
      membershipPlan: additionalData.membershipPlan || (isActive ? 'annual' : 'cancelled'),
      supabaseId: user.id,
      tags
    };

    const result = await updateUserWithFullData(userData, listId);
    // console.log('Successfully updated subscription status in Mailchimp:', result);
    return result;
  } catch (error) {
    console.error('Failed to update subscription status in Mailchimp:', error);
    return { success: false, error: error.message };
  }
}; 